/**
 * What-If Schema Sandbox editor (doc 147 §6.5).
 *
 * Three-pane workflow: sandbox list (left), op-by-op editor (centre), live
 * diff preview (right). The backend (`sandboxes.ts`) treats ops as opaque
 * data; the schema lives in `state/sandboxOps.ts` and the renderer here
 * builds + previews ops without touching the real manifest.
 *
 * Out of scope for v1 (tracked separately):
 *   - Constellation overlay showing the rippled diff on the graph itself
 *   - CodeMirror-based "raw JSON" editor mode
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import type {
  AtlasManifest,
  CloudFunctionKind,
  EventSafetyTier,
  Field,
  ReferenceKind,
  SurfaceType,
} from '@atlas-manifest/types';
import type { Sandbox, SandboxStatus } from '../state/atlasApiTypes.js';
import {
  applyOps,
  diffEntity,
  validateOp,
  normalizeSandboxOps,
  type SandboxOp,
  type SandboxOpEntry,
} from '../state/sandboxOps.js';
import { overlayDiff } from '../state/sandboxOverlay.js';
import { domainById, entityById, surfaceById, eventTypeById, cloudFunctionById } from '../state/selectors.js';
import { buildDraftForObject, EMPTY_DRAFT, type EditableKind, type OpDraft } from '../state/opDraft.js';
import { Icon } from '../components/Icon.js';
import { ErrorState } from '../components/ErrorState.js';
import { AiMarkdown } from '../components/AiMarkdown.js';
import { AiThinking } from '../components/AiThinking.js';
import { EntityChip } from '../components/EntityChip.js';
import { ObjectContextHeader, KIND_LABEL } from '../components/ObjectContextHeader.js';
import { ObjectPicker } from '../components/ObjectPicker.js';
import { listPickable } from '../state/pickableObjects.js';
import { useAiSuggest } from '../state/useAiSuggest.js';
import { refactorProposalDraftPrompt } from '../state/aiPrompts.js';
import type { AiSuggestOptions } from '../state/aiPrompts.js';
import type { AskAtlasResult } from '../state/atlasApiTypes.js';
import type { Route, SeedProposal } from '../state/hashRouter.js';
import { navigateSimulateSandbox, takeSeedOp } from '../state/hashRouter.js';
import { streamAskAtlas } from '../firebase/atlasApi.js';

export interface SandboxEditorViewProps {
  manifest: AtlasManifest;
  sandboxes: Sandbox[];
  activeSandboxId: string | undefined;
  onSelectSandbox: (id: string | undefined) => void;
  onSaveSandbox: (sandboxId: string, name: string, ops: unknown[], createdFromCommit?: string) => Promise<void>;
  onDeleteSandbox: (sandboxId: string) => Promise<void>;
  onNavigateEntity: (entityId: string) => void;
  /** Optional entity id to preload into the draft op (from `#sandbox/seed/<id>`). */
  seedEntity?: string;
  /** Optional non-entity object to preload into the draft op (from `#sandbox/seed/<kind>/<id>`). */
  seedObject?: { kind: 'surface' | 'cloud-function' | 'event-type' | 'firestore-path'; id: string };
  /** Proposal context passed in-memory from RefactorProposalsView (Q4). */
  seedProposal?: SeedProposal;
  /** Injected in tests; defaults to streamAskAtlas. */
  onAsk?: (opts: AiSuggestOptions) => Promise<AskAtlasResult>;
  onSubmitForReview?: (sandboxId: string) => Promise<{ ok: true; sandboxId: string } | { ok: false; error: string }>;
  onNavigate?: (route: Route) => void;
}

const STATUS_LABEL: Record<SandboxStatus, string> = {
  draft: 'Draft',
  under_review: 'Under review',
  published: 'Published',
};

type OpKind = SandboxOp['kind'];

type EditorMode = 'edit' | 'create';
const CREATE_KINDS: readonly OpKind[] = [
  'create-entity', 'create-domain', 'create-reference', 'create-surface',
  'create-cloud-function', 'create-event', 'create-path', 'create-tag', 'create-state-machine',
];
/** Action cards per object kind, in display order. Each maps to the OpKind
 *  its "default" sub-action stages into `draft` when clicked, plus any
 *  extra OpDraft fields that op kind needs beyond the selector Step 2 sets
 *  from the picked object (e.g. which owner kind a payload-field edit
 *  targets, which array a function-ref edit targets). Entity's "Edit
 *  fields" and Surface/EventType's "Edit payload fields" land on
 *  add-field / add-payload-field as the default, with the existing
 *  add/remove/rename/change-type toggle inside that section unchanged. */
const ACTIONS_FOR_KIND: Record<EditableKind, { label: string; sub: string; kind: OpKind; extra?: Partial<OpDraft> }[]> = {
  entity: [
    { label: 'Edit details', sub: 'name · domain · description', kind: 'update-entity' },
    { label: 'Edit fields', sub: 'add / remove / rename / retype', kind: 'add-field' },
  ],
  surface: [
    { label: 'Edit details', sub: 'type · domain · hub · description', kind: 'update-surface' },
    {
      label: 'Edit payload fields', sub: 'add / remove / rename / retype', kind: 'add-payload-field',
      extra: { payloadFieldOwnerKind: 'surface' },
    },
  ],
  'cloud-function': [
    { label: 'Edit details', sub: 'kind · domain · description · rationale', kind: 'update-cloud-function' },
    { label: 'Edit reads', sub: 'entities this function reads', kind: 'add-function-entity-ref', extra: { refArrayName: 'reads' } },
    { label: 'Edit writes', sub: 'entities this function writes', kind: 'add-function-entity-ref', extra: { refArrayName: 'writes' } },
  ],
  'event-type': [
    { label: 'Edit details', sub: 'category · description · retention', kind: 'update-event' },
    {
      label: 'Edit payload fields', sub: 'add / remove / rename / retype', kind: 'add-payload-field',
      extra: { payloadFieldOwnerKind: 'event' },
    },
    { label: 'Edit carried entities', sub: 'entities this event carries', kind: 'add-event-carried-entity' },
  ],
  'firestore-path': [{ label: 'Edit details', sub: 'retention · owner · security note', kind: 'update-path' }],
};

const SURFACE_TYPES: readonly SurfaceType[] = [
  'card', 'chart', 'overlay', 'chip', 'ai', 'hero', 'list', 'sheet', 'banner', 'grid', 'other',
];
const CLOUD_FUNCTION_KINDS: readonly CloudFunctionKind[] = ['callable', 'http', 'trigger', 'scheduled'];
const REFERENCE_KINDS: readonly ReferenceKind[] = ['fk', 'embed', 'enum', 'aggregate', 'event'];
const EVENT_SAFETY_TIERS: readonly EventSafetyTier[] = [0, 1, 2, 3];

/** R1.H3 — raw JSON editor caps. 50 KB / 1000 ops is enough for any
 *  realistic sandbox; bounded so a hostile paste can't hang the page. */
const MAX_RAW_INPUT_BYTES = 50 * 1024;
const MAX_RAW_OPS = 1000;

const ALL_EDITABLE_KINDS: readonly EditableKind[] = ['entity', 'surface', 'cloud-function', 'event-type', 'firestore-path'];

export function draftToOp(d: OpDraft): SandboxOp | null {
  switch (d.kind) {
    case 'add-field':
      return d.entityId && d.fieldName && d.addType
        ? {
            kind: 'add-field',
            entityId: d.entityId,
            field: { name: d.fieldName, type: d.addType, optional: d.optional },
          }
        : null;
    case 'remove-field':
      return d.entityId && d.fieldName
        ? { kind: 'remove-field', entityId: d.entityId, fieldName: d.fieldName }
        : null;
    case 'rename-field':
      return d.entityId && d.from && d.to
        ? { kind: 'rename-field', entityId: d.entityId, from: d.from, to: d.to }
        : null;
    case 'change-type':
      return d.entityId && d.fieldName && d.newType
        ? { kind: 'change-type', entityId: d.entityId, fieldName: d.fieldName, newType: d.newType }
        : null;
    case 'create-entity':
      return d.newId && d.newName && d.newDomain && d.newDescription
        ? { kind: 'create-entity', entityId: d.newId, name: d.newName, domain: d.newDomain, description: d.newDescription }
        : null;
    case 'create-domain':
      return d.newId && d.newName && d.newColor && d.newDescription
        ? { kind: 'create-domain', domainId: d.newId, name: d.newName, color: d.newColor, description: d.newDescription }
        : null;
    case 'create-reference':
      return d.newFromEntity && d.newToEntity && d.newFromField && d.newReferenceKind
        ? {
            kind: 'create-reference',
            from: d.newFromEntity,
            to: d.newToEntity,
            fromField: d.newFromField,
            referenceKind: d.newReferenceKind as ReferenceKind,
          }
        : null;
    case 'create-surface':
      return d.newId && d.newSurfaceType && d.newDomain && d.newHub
        ? {
            kind: 'create-surface',
            surfaceId: d.newId,
            surfaceType: d.newSurfaceType as SurfaceType,
            domain: d.newDomain,
            hub: d.newHub,
          }
        : null;
    case 'create-cloud-function':
      return d.newId && d.newFunctionKind && d.newDomain
        ? {
            kind: 'create-cloud-function',
            name: d.newId,
            functionKind: d.newFunctionKind as CloudFunctionKind,
            domain: d.newDomain,
          }
        : null;
    case 'create-event':
      return d.newId && d.newCategory && d.newSafetyTier
        ? { kind: 'create-event', eventType: d.newId, category: d.newCategory, safetyTier: Number(d.newSafetyTier) as 0 | 1 | 2 | 3 }
        : null;
    case 'create-path':
      return d.entityId && d.newPath && d.newRetention
        ? { kind: 'create-path', entityId: d.entityId, path: d.newPath, retention: d.newRetention }
        : null;
    case 'create-tag':
      return d.newTag && d.newDescription
        ? { kind: 'create-tag', tag: d.newTag, description: d.newDescription }
        : null;
    case 'create-state-machine':
      return d.newId && d.newName && d.newDomain
        ? { kind: 'create-state-machine', machineId: d.newId, name: d.newName, domain: d.newDomain }
        : null;
    case 'update-entity': {
      const patch: Partial<{ name: string; domain: string; description: string }> = {};
      if (d.updateName) patch.name = d.updateName;
      if (d.updateDomain) patch.domain = d.updateDomain;
      if (d.updateDescription) patch.description = d.updateDescription;
      return d.entityId && Object.keys(patch).length > 0
        ? { kind: 'update-entity', entityId: d.entityId, ...patch }
        : null;
    }
    case 'update-surface': {
      const patch: Partial<{ surfaceType: SurfaceType; domain: string; hub: string; description: string }> = {};
      if (d.updateSurfaceType) patch.surfaceType = d.updateSurfaceType as SurfaceType;
      if (d.updateDomain) patch.domain = d.updateDomain;
      if (d.updateHub) patch.hub = d.updateHub;
      if (d.updateDescription) patch.description = d.updateDescription;
      return d.updateSelector && Object.keys(patch).length > 0
        ? { kind: 'update-surface', surfaceId: d.updateSelector, ...patch }
        : null;
    }
    case 'update-cloud-function': {
      const patch: Partial<{
        functionKind: CloudFunctionKind;
        domain: string;
        description: string;
        rationale: string;
        triggerCondition: string;
      }> = {};
      if (d.updateFunctionKind) patch.functionKind = d.updateFunctionKind as CloudFunctionKind;
      if (d.updateDomain) patch.domain = d.updateDomain;
      if (d.updateDescription) patch.description = d.updateDescription;
      if (d.updateRationale) patch.rationale = d.updateRationale;
      if (d.updateTriggerCondition) patch.triggerCondition = d.updateTriggerCondition;
      return d.updateSelector && Object.keys(patch).length > 0
        ? { kind: 'update-cloud-function', name: d.updateSelector, ...patch }
        : null;
    }
    case 'update-event': {
      const patch: Partial<{ category: string; description: string; businessTrigger: string; retentionDays: number }> = {};
      if (d.updateCategory) patch.category = d.updateCategory;
      if (d.updateDescription) patch.description = d.updateDescription;
      if (d.updateBusinessTrigger) patch.businessTrigger = d.updateBusinessTrigger;
      if (d.updateRetentionDays) patch.retentionDays = Number(d.updateRetentionDays);
      return d.updateSelector && Object.keys(patch).length > 0
        ? { kind: 'update-event', type: d.updateSelector, ...patch }
        : null;
    }
    case 'update-path': {
      const patch: Partial<{ retention: string; securityRuleNote: string; owner: string; quotaNotes: string }> = {};
      if (d.updateRetention) patch.retention = d.updateRetention;
      if (d.updateSecurityRuleNote) patch.securityRuleNote = d.updateSecurityRuleNote;
      if (d.updateOwner) patch.owner = d.updateOwner;
      if (d.updateQuotaNotes) patch.quotaNotes = d.updateQuotaNotes;
      return d.updateSelector && Object.keys(patch).length > 0
        ? { kind: 'update-path', path: d.updateSelector, ...patch }
        : null;
    }
    case 'add-payload-field':
      return d.updateSelector && d.payloadFieldOwnerKind && d.fieldName && d.addType
        ? {
            kind: 'add-payload-field',
            ownerKind: d.payloadFieldOwnerKind,
            ownerId: d.updateSelector,
            field: { name: d.fieldName, type: d.addType },
          }
        : null;
    case 'remove-payload-field':
      return d.updateSelector && d.payloadFieldOwnerKind && d.fieldName
        ? { kind: 'remove-payload-field', ownerKind: d.payloadFieldOwnerKind, ownerId: d.updateSelector, fieldName: d.fieldName }
        : null;
    case 'rename-payload-field':
      return d.updateSelector && d.payloadFieldOwnerKind && d.from && d.to
        ? { kind: 'rename-payload-field', ownerKind: d.payloadFieldOwnerKind, ownerId: d.updateSelector, from: d.from, to: d.to }
        : null;
    case 'change-payload-field-type':
      return d.updateSelector && d.payloadFieldOwnerKind && d.fieldName && d.newType
        ? {
            kind: 'change-payload-field-type',
            ownerKind: d.payloadFieldOwnerKind,
            ownerId: d.updateSelector,
            fieldName: d.fieldName,
            newType: d.newType,
          }
        : null;
    case 'add-function-entity-ref':
      return d.updateSelector && d.refArrayName && d.refEntityId
        ? { kind: 'add-function-entity-ref', name: d.updateSelector, arrayName: d.refArrayName, entityId: d.refEntityId }
        : null;
    case 'remove-function-entity-ref':
      return d.updateSelector && d.refArrayName && d.refEntityId
        ? { kind: 'remove-function-entity-ref', name: d.updateSelector, arrayName: d.refArrayName, entityId: d.refEntityId }
        : null;
    case 'add-event-carried-entity':
      return d.updateSelector && d.refEntityId
        ? { kind: 'add-event-carried-entity', type: d.updateSelector, entityId: d.refEntityId }
        : null;
    case 'remove-event-carried-entity':
      return d.updateSelector && d.refEntityId
        ? { kind: 'remove-event-carried-entity', type: d.updateSelector, entityId: d.refEntityId }
        : null;
    case 'link-feature-flag':
    case 'unlink-feature-flag':
      // Never authored through the manual draft form — Confirm/Ask Atlas (Task 8/9)
      // construct these directly and hand them to navigateSeedOp.
      return null;
  }
}

/**
 * Plain-text summary for ops list keys and logging. Does not render
 * entity IDs as clickable — use SummarizeOpRender for that.
 */
function summarizeOpText(op: SandboxOp): string {
  switch (op.kind) {
    case 'add-field':
      return `add ${op.entityId}.${op.field.name}: ${op.field.type}`;
    case 'remove-field':
      return `remove ${op.entityId}.${op.fieldName}`;
    case 'rename-field':
      return `rename ${op.entityId}.${op.from} → ${op.to}`;
    case 'change-type':
      return `retype ${op.entityId}.${op.fieldName} → ${op.newType}`;
    case 'create-entity':
      return `create entity ${op.entityId} (${op.domain})`;
    case 'create-domain':
      return `create domain ${op.domainId}`;
    case 'create-reference':
      return `create reference ${op.from}.${op.fromField} → ${op.to}`;
    case 'create-surface':
      return `create surface ${op.surfaceId} (${op.domain})`;
    case 'create-cloud-function':
      return `create function ${op.name} (${op.domain})`;
    case 'create-event':
      return `create event ${op.eventType} (${op.category})`;
    case 'create-path':
      return `create path ${op.path} (${op.entityId})`;
    case 'create-tag':
      return `create tag ${op.tag}`;
    case 'create-state-machine':
      return `create state machine ${op.machineId} (${op.domain})`;
    case 'update-entity':
      return `update entity ${op.entityId}`;
    case 'update-surface':
      return `update surface ${op.surfaceId}`;
    case 'update-cloud-function':
      return `update function ${op.name}`;
    case 'update-event':
      return `update event ${op.type}`;
    case 'update-path':
      return `update path ${op.path}`;
    case 'add-payload-field':
      return `add ${op.ownerKind} ${op.ownerId}.${op.field.name}: ${op.field.type}`;
    case 'remove-payload-field':
      return `remove ${op.ownerKind} ${op.ownerId}.${op.fieldName}`;
    case 'rename-payload-field':
      return `rename ${op.ownerKind} ${op.ownerId}.${op.from} → ${op.to}`;
    case 'change-payload-field-type':
      return `retype ${op.ownerKind} ${op.ownerId}.${op.fieldName} → ${op.newType}`;
    case 'add-function-entity-ref':
      return `add ${op.entityId} to ${op.name}.${op.arrayName}`;
    case 'remove-function-entity-ref':
      return `remove ${op.entityId} from ${op.name}.${op.arrayName}`;
    case 'add-event-carried-entity':
      return `add ${op.entityId} to ${op.type} carried entities`;
    case 'remove-event-carried-entity':
      return `remove ${op.entityId} from ${op.type} carried entities`;
    case 'link-feature-flag':
      return `link ${op.featureId} ↔ ${op.flagId}`;
    case 'unlink-feature-flag':
      return `unlink ${op.featureId} ↔ ${op.flagId}`;
  }
}

/**
 * Render an op summary with entity IDs as clickable EntityChips.
 * Returns JSX with embedded chips for every entity-id reference in the op.
 */
function SummarizeOpRender({ op, manifest }: { op: SandboxOp; manifest: AtlasManifest }): React.JSX.Element {
  switch (op.kind) {
    case 'add-field':
      return (
        <>
          add <EntityChip entityId={op.entityId} manifest={manifest} size="sm" />.{op.field.name}: {op.field.type}
        </>
      );
    case 'remove-field':
      return (
        <>
          remove <EntityChip entityId={op.entityId} manifest={manifest} size="sm" />.{op.fieldName}
        </>
      );
    case 'rename-field':
      return (
        <>
          rename <EntityChip entityId={op.entityId} manifest={manifest} size="sm" />.{op.from} → {op.to}
        </>
      );
    case 'change-type':
      return (
        <>
          retype <EntityChip entityId={op.entityId} manifest={manifest} size="sm" />.{op.fieldName} → {op.newType}
        </>
      );
    case 'create-entity':
      return (
        <>
          create entity <EntityChip entityId={op.entityId} manifest={manifest} size="sm" /> ({op.domain})
        </>
      );
    case 'create-domain':
      return <>create domain {op.domainId}</>;
    case 'create-reference':
      return (
        <>
          create reference <EntityChip entityId={op.from} manifest={manifest} size="sm" />.{op.fromField} → <EntityChip entityId={op.to} manifest={manifest} size="sm" />
        </>
      );
    case 'create-surface':
      return (
        <>
          create surface {op.surfaceId} ({op.domain})
        </>
      );
    case 'create-cloud-function':
      return (
        <>
          create function {op.name} ({op.domain})
        </>
      );
    case 'create-event':
      return (
        <>
          create event {op.eventType} ({op.category})
        </>
      );
    case 'create-path':
      return (
        <>
          create path {op.path} (<EntityChip entityId={op.entityId} manifest={manifest} size="sm" />)
        </>
      );
    case 'create-tag':
      return <>create tag {op.tag}</>;
    case 'create-state-machine':
      return (
        <>
          create state machine {op.machineId} ({op.domain})
        </>
      );
    case 'update-entity':
      return (
        <>
          update <EntityChip entityId={op.entityId} manifest={manifest} size="sm" />
        </>
      );
    case 'update-surface':
      return <>update surface {op.surfaceId}</>;
    case 'update-cloud-function':
      return <>update function {op.name}</>;
    case 'update-event':
      return <>update event {op.type}</>;
    case 'update-path':
      return <>update path {op.path}</>;
    case 'add-payload-field':
      return <>add {op.ownerKind} {op.ownerId}.{op.field.name}: {op.field.type}</>;
    case 'remove-payload-field':
      return <>remove {op.ownerKind} {op.ownerId}.{op.fieldName}</>;
    case 'rename-payload-field':
      return <>rename {op.ownerKind} {op.ownerId}.{op.from} → {op.to}</>;
    case 'change-payload-field-type':
      return <>retype {op.ownerKind} {op.ownerId}.{op.fieldName} → {op.newType}</>;
    case 'add-function-entity-ref':
      return <>add <EntityChip entityId={op.entityId} manifest={manifest} size="sm" /> to {op.name}.{op.arrayName}</>;
    case 'remove-function-entity-ref':
      return <>remove <EntityChip entityId={op.entityId} manifest={manifest} size="sm" /> from {op.name}.{op.arrayName}</>;
    case 'add-event-carried-entity':
      return <>add <EntityChip entityId={op.entityId} manifest={manifest} size="sm" /> to {op.type} carried entities</>;
    case 'remove-event-carried-entity':
      return <>remove <EntityChip entityId={op.entityId} manifest={manifest} size="sm" /> from {op.type} carried entities</>;
    case 'link-feature-flag':
      return <>link {op.featureId} ↔ {op.flagId}</>;
    case 'unlink-feature-flag':
      return <>unlink {op.featureId} ↔ {op.flagId}</>;
  }
}

export function SandboxEditorView({
  manifest,
  sandboxes,
  activeSandboxId,
  onSelectSandbox,
  onSaveSandbox,
  onDeleteSandbox,
  onNavigateEntity,
  seedEntity,
  seedObject,
  seedProposal,
  onAsk,
  onSubmitForReview,
  onNavigate,
}: SandboxEditorViewProps): React.JSX.Element {
  const active = activeSandboxId
    ? sandboxes.find((s) => s.id === activeSandboxId)
    : undefined;

  // Seed the draft from a `#sandbox/seed/<entityId>` deep-link so the
  // "Sandbox a change to X" palette command lands the user one form
  // away from a meaningful proposal. Also supports `#sandbox/seed/<kind>/<id>`
  // for non-entity objects.
  const [mode, setMode] = useState<EditorMode>('edit');
  const [browseKind, setBrowseKind] = useState<EditableKind>('entity');
  const [selectedObject, setSelectedObject] = useState<{ kind: EditableKind; id: string } | undefined>(() => {
    if (seedObject) return { kind: seedObject.kind, id: seedObject.id };
    if (seedEntity) return { kind: 'entity', id: seedEntity };
    return undefined;
  });
  const [draft, setDraft] = useState<OpDraft>(() =>
    selectedObject ? buildDraftForObject(selectedObject.kind, selectedObject.id, manifest) : EMPTY_DRAFT,
  );

  const selectObject = useCallback(
    (kind: EditableKind, id: string) => {
      setSelectedObject({ kind, id });
      setDraft(buildDraftForObject(kind, id, manifest));
    },
    [manifest],
  );
  const [ops, setOps] = useState<SandboxOpEntry[]>([]);
  const rawOps = useMemo(() => ops.map((e) => e.op), [ops]);

  const [bannerDismissed, setBannerDismissed] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const defaultOnAsk = useCallback(
    (opts: AiSuggestOptions) => streamAskAtlas(opts.prompt, opts.summary, opts.knownEntityIds),
    [],
  );
  const ai = useAiSuggest(onAsk ?? defaultOnAsk);

  const seedAsProposal = seedProposal
    ? {
        kind: seedProposal.kind,
        entityId: seedProposal.entityId,
        ref: seedProposal.ref,
        title: `Fix: ${seedProposal.entityId} (${seedProposal.kind})`,
        rationale: seedProposal.rationale,
        confidence: 1,
      }
    : null;

  // Consume a pending seeded op entry on mount (from navigateSeedOp).
  useEffect(() => {
    const seeded = takeSeedOp();
    if (!seeded) return;
    const id = `sb-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    setOps([seeded]);
    onSelectSandbox(id);
    void onSaveSandbox(id, `AI edit: ${summarizeOpText(seeded.op)}`, [seeded], manifest.generatedFromCommit);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Reset local op list when the active sandbox changes — sourced from the
  // sandbox's persisted ops (migration-safe: raw SandboxOp[] read as all-accepted).
  const persistedOpsKey = activeSandboxId ?? '__none__';
  const [lastKey, setLastKey] = useState<string>('__none__');
  if (persistedOpsKey !== lastKey) {
    setOps(normalizeSandboxOps(Array.isArray(active?.ops) ? active!.ops : []));
    setLastKey(persistedOpsKey);
  }

  const afterManifest = useMemo(() => applyOps(manifest, rawOps), [manifest, rawOps]);
  const touchedEntities = useMemo(
    () =>
      Array.from(
        new Set(
          rawOps
            .filter((o): o is Extract<SandboxOp, { entityId: string }> =>
              'entityId' in o && typeof o.entityId === 'string',
            )
            .map((o) => o.entityId),
        ),
      ),
    [rawOps],
  );
  const touchedDomains = useMemo(
    () =>
      Array.from(
        new Set(
          rawOps
            .filter((o): o is Extract<SandboxOp, { kind: 'create-domain' }> => o.kind === 'create-domain')
            .map((o) => o.domainId),
        ),
      ),
    [rawOps],
  );
  const createdReferences = useMemo(
    () =>
      rawOps.filter(
        (o): o is Extract<SandboxOp, { kind: 'create-reference' }> => o.kind === 'create-reference',
      ),
    [rawOps],
  );
  const createdSurfaceIds = useMemo(
    () =>
      Array.from(
        new Set(
          rawOps
            .filter((o): o is Extract<SandboxOp, { kind: 'create-surface' }> => o.kind === 'create-surface')
            .map((o) => o.surfaceId),
        ),
      ),
    [rawOps],
  );
  const createdFunctionNames = useMemo(
    () =>
      Array.from(
        new Set(
          rawOps
            .filter((o): o is Extract<SandboxOp, { kind: 'create-cloud-function' }> => o.kind === 'create-cloud-function')
            .map((o) => o.name),
        ),
      ),
    [rawOps],
  );
  const createdEventTypes = useMemo(
    () =>
      Array.from(
        new Set(
          rawOps
            .filter((o): o is Extract<SandboxOp, { kind: 'create-event' }> => o.kind === 'create-event')
            .map((o) => o.eventType),
        ),
      ),
    [rawOps],
  );
  const createdPaths = useMemo(
    () =>
      Array.from(
        new Set(
          rawOps
            .filter((o): o is Extract<SandboxOp, { kind: 'create-path' }> => o.kind === 'create-path')
            .map((o) => o.path),
        ),
      ),
    [rawOps],
  );
  const createdTags = useMemo(
    () =>
      Array.from(
        new Set(
          rawOps.filter((o): o is Extract<SandboxOp, { kind: 'create-tag' }> => o.kind === 'create-tag').map((o) => o.tag),
        ),
      ),
    [rawOps],
  );
  const createdMachineIds = useMemo(
    () =>
      Array.from(
        new Set(
          rawOps
            .filter((o): o is Extract<SandboxOp, { kind: 'create-state-machine' }> => o.kind === 'create-state-machine')
            .map((o) => o.machineId),
        ),
      ),
    [rawOps],
  );
  const overlay = useMemo(() => overlayDiff(manifest, rawOps), [manifest, rawOps]);

  // PR Q4 — raw JSON editor mode toggle. Holds the textarea contents
  // independently so parse errors don't blow away the user's typing.
  const [rawMode, setRawMode] = useState(false);
  const [rawText, setRawText] = useState<string>('');
  const [rawError, setRawError] = useState<string | null>(null);

  const draftedOp = draftToOp(draft);
  const draftedIsValid = draftedOp !== null;

  const openRaw = (): void => {
    setRawText(JSON.stringify(rawOps, null, 2));
    setRawError(null);
    setRawMode(true);
  };
  const applyRaw = (): void => {
    try {
      // R1.H3 — input cap. 50 KB is generous for hand-edited op lists
      // but bounded enough that JSON.parse can't hang on a megabyte paste.
      if (rawText.length > MAX_RAW_INPUT_BYTES) {
        throw new Error(`Input exceeds ${MAX_RAW_INPUT_BYTES} chars; trim it down.`);
      }
      const parsed = JSON.parse(rawText);
      if (!Array.isArray(parsed)) throw new Error('Top-level must be an array of ops.');
      if (parsed.length > MAX_RAW_OPS) {
        throw new Error(`Too many ops (${parsed.length}); cap is ${MAX_RAW_OPS}.`);
      }
      const valid: SandboxOp[] = [];
      for (const item of parsed) {
        const v = validateOp(item as SandboxOp);
        if (!v.ok) throw new Error(v.reason);
        valid.push(item as SandboxOp);
      }
      // Raw-edited ops are all treated as accepted — the raw JSON editor has
      // no UI for proposed/confidence state, so anything typed there is final.
      setOps(valid.map((op) => ({ op, state: 'accepted' as const })));
      setRawMode(false);
      setRawError(null);
    } catch (e) {
      setRawError(e instanceof Error ? e.message : 'Invalid JSON');
    }
  };

  const newSandbox = (): void => {
    const id = `sb-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    setOps([]);
    onSelectSandbox(id);
  };

  const addOp = (): void => {
    if (!draftedOp) return;
    setOps([...ops, { op: draftedOp, state: 'accepted' }]);
    setDraft({ ...EMPTY_DRAFT, kind: draft.kind });
  };

  const removeOp = (idx: number): void => {
    setOps(ops.filter((_, i) => i !== idx));
  };

  const save = (): void => {
    if (!activeSandboxId || ops.length === 0) return;
    const name = active?.name ?? `Untitled sandbox ${activeSandboxId.slice(0, 6)}`;
    void onSaveSandbox(activeSandboxId, name, ops, !active ? manifest.generatedFromCommit : undefined);
  };

  const remove = (): void => {
    if (!activeSandboxId) return;
    const status = active?.status ?? 'draft';
    const message =
      status === 'draft'
        ? 'Delete this sandbox? Cannot be undone.'
        : `This sandbox is ${STATUS_LABEL[status].toLowerCase()} — deleting it will orphan its ${status === 'under_review' ? 'review' : 'publish'} page. Delete anyway? Cannot be undone.`;
    if (!window.confirm(message)) return;
    void onDeleteSandbox(activeSandboxId);
    onSelectSandbox(undefined);
  };

  const currentStatus = active?.status ?? 'draft';

  const submitForReview = useCallback(async () => {
    if (!activeSandboxId) return;
    setSubmitError(null);
    try {
      const result = await onSubmitForReview?.(activeSandboxId);
      if (result?.ok) {
        onNavigate?.({ kind: 'review', sandboxId: activeSandboxId });
      } else {
        setSubmitError(result?.error ?? 'Failed to submit sandbox for review');
      }
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'An error occurred');
    }
  }, [activeSandboxId, onSubmitForReview, onNavigate]);

  const getOpState = (entry: SandboxOpEntry): 'proposed' | 'accepted' | 'readonly' => {
    if (active?.status && active.status !== 'draft') return 'readonly';
    return entry.state;
  };

  return (
    <div className="sandbox-editor">
      {seedProposal && !bannerDismissed && (
        <section
          className="seed-proposal-banner"
          aria-label="proposal context"
          role="region"
        >
          <div className="seed-proposal-info">
            <strong>{seedProposal.entityId}</strong>
            <span className="seed-proposal-kind">{seedProposal.kind}</span>
            <p className="seed-proposal-rationale">{seedProposal.rationale}</p>
          </div>

          <div className="seed-proposal-actions">
            {onAsk !== undefined && seedAsProposal && (
              <button
                className="btn btn-primary btn-sm"
                onClick={() => void ai.suggest(refactorProposalDraftPrompt(seedAsProposal, manifest))}
                disabled={ai.busy}
              >
                ✦ Draft with AI
              </button>
            )}
            <button
              className="btn btn-ghost btn-sm"
              aria-label="dismiss"
              onClick={() => setBannerDismissed(true)}
            >
              Dismiss
            </button>
          </div>

          {ai.busy && <AiThinking label="Drafting a proposal…" />}
          {ai.answer && (
            <div className="seed-proposal-draft">
              <AiMarkdown text={ai.answer} />
              <button className="btn btn-ghost btn-xs" onClick={ai.clear}>Clear</button>
            </div>
          )}
          {ai.error && (
            <ErrorState
              variant="inline"
              message={ai.error}
              onDismiss={ai.clear}
              onRetry={seedAsProposal ? () => void ai.suggest(refactorProposalDraftPrompt(seedAsProposal, manifest)) : undefined}
            />
          )}
        </section>
      )}
      <aside className="sandbox-list" aria-label="Sandboxes">
        <header className="sandbox-list-head">
          <h2>Sandboxes</h2>
          <button className="btn btn-primary" onClick={newSandbox}>
            <Icon name="add" size="sm" /> New sandbox
          </button>
          {onNavigate && (
            <button
              className="btn btn-ghost"
              onClick={() => onNavigate({ kind: 'new-design-change' })}
            >
              <Icon name="design_services" size="sm" /> New Design Change
            </button>
          )}
        </header>
        {sandboxes.length === 0 ? (
          <p className="sandbox-empty">No sandboxes yet. Create one to sketch a schema change.</p>
        ) : (
          <ul>
            {sandboxes.map((s) => {
              const status = s.status ?? 'draft';
              return (
                <li key={s.id} className="sandbox-list-row">
                  <button
                    className={`sandbox-list-item${s.id === activeSandboxId ? ' is-active' : ''}`}
                    onClick={() => onSelectSandbox(s.id)}
                  >
                    <div className="sandbox-name">{s.name}</div>
                    <div className="sandbox-meta">
                      {s.ops.length} op{s.ops.length === 1 ? '' : 's'}
                      <span className={`sandbox-status-badge sandbox-status-${status}`}>
                        {STATUS_LABEL[status]}
                      </span>
                    </div>
                  </button>
                  {onNavigate && status === 'under_review' && (
                    <button
                      type="button"
                      className="btn btn-ghost btn-xs sandbox-status-link"
                      onClick={(e) => {
                        e.stopPropagation();
                        onNavigate({ kind: 'review', sandboxId: s.id });
                      }}
                    >
                      View review →
                    </button>
                  )}
                  {onNavigate && status === 'published' && (
                    <button
                      type="button"
                      className="btn btn-ghost btn-xs sandbox-status-link"
                      onClick={(e) => {
                        e.stopPropagation();
                        onNavigate({ kind: 'publish', sandboxId: s.id });
                      }}
                    >
                      View publish →
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </aside>

      {activeSandboxId ? (
        <>
          <section className="sandbox-editor-form" aria-label="Op editor">
            <header className="sandbox-editor-head">
              <h3>{active?.name ?? 'New sandbox'}</h3>
              <div className="sandbox-actions">
                <button
                  className="btn btn-primary"
                  onClick={save}
                  disabled={ops.length === 0}
                >
                  Save
                </button>
                <button
                  className="btn btn-primary"
                  onClick={submitForReview}
                  disabled={currentStatus !== 'draft'}
                  title={currentStatus !== 'draft' ? `Sandbox is ${currentStatus}, cannot submit` : undefined}
                >
                  Submit for review
                </button>
                <button
                  className="btn btn-ghost"
                  onClick={() => activeSandboxId && navigateSimulateSandbox(activeSandboxId)}
                >
                  <Icon name="play_circle" size="sm" /> Simulate
                </button>
                <button className="btn btn-ghost" onClick={remove} aria-label="Delete sandbox">
                  <Icon name="delete" size="sm" /> Delete
                </button>
              </div>
            </header>

            {submitError && (
              <div className="error-banner" style={{ marginTop: 'var(--sp-md)' }}>
                {submitError}
              </div>
            )}

            <fieldset className="sandbox-op-builder">
              <legend>New op</legend>
              {selectedObject && draft.kind.startsWith('update-') && (
                <ObjectContextHeader
                  kind={selectedObject.kind}
                  id={selectedObject.id}
                  subtitle={listPickable(manifest, selectedObject.kind).find((p) => p.id === selectedObject.id)?.subtitle ?? ''}
                  pickableItems={ALL_EDITABLE_KINDS.flatMap((k) => listPickable(manifest, k))}
                  onChangeObject={selectObject}
                />
              )}
              <div className="editor-mode-toggle" role="group" aria-label="Editor mode">
                <button type="button" className={mode === 'edit' ? 'active' : ''} onClick={() => setMode('edit')}>Edit existing</button>
                <button type="button" className={mode === 'create' ? 'active' : ''} onClick={() => setMode('create')}>Create new</button>
              </div>

              {mode === 'edit' && !selectedObject && (
                <div className="editor-object-browse">
                  <div className="kind-chip-row">
                    {(Object.keys(ACTIONS_FOR_KIND) as EditableKind[]).map((k) => (
                      <button
                        key={k}
                        type="button"
                        className={`kind-chip${browseKind === k ? ' active' : ''}`}
                        onClick={() => setBrowseKind(k)}
                      >
                        {KIND_LABEL[k]}
                      </button>
                    ))}
                  </div>
                  <ObjectPicker
                    items={listPickable(manifest, browseKind)}
                    ariaLabel={`Search ${KIND_LABEL[browseKind]} objects to edit`}
                    placeholder={`Search ${KIND_LABEL[browseKind]}…`}
                    onChange={(id) => selectObject(browseKind, id)}
                  />
                </div>
              )}

              {mode === 'edit' && selectedObject && (
                <div className="action-cards">
                  {ACTIONS_FOR_KIND[selectedObject.kind].map((a, i) => (
                    <button
                      key={`${a.kind}-${i}`}
                      type="button"
                      className={`action-card${draft.kind === a.kind ? ' selected' : ''}`}
                      onClick={() =>
                        setDraft((d) => ({
                          ...d,
                          kind: a.kind,
                          updateSelector: selectedObject.id,
                          entityId: selectedObject.kind === 'entity' ? selectedObject.id : d.entityId,
                          ...a.extra,
                        }))
                      }
                    >
                      <div className="action-card-title">{a.label}</div>
                      <div className="action-card-sub">{a.sub}</div>
                    </button>
                  ))}
                </div>
              )}

              {mode === 'create' && (
                <label>
                  Object type
                  <select value={draft.kind} onChange={(e) => setDraft({ ...EMPTY_DRAFT, kind: e.target.value as OpKind })}>
                    {CREATE_KINDS.map((k) => <option key={k} value={k}>{k}</option>)}
                  </select>
                </label>
              )}
              {!(
                [
                  'create-entity',
                  'create-domain',
                  'create-reference',
                  'create-surface',
                  'create-cloud-function',
                  'create-event',
                  'create-tag',
                  'create-state-machine',
                ] as OpKind[]
              ).includes(draft.kind) && (
                <label>
                  Entity
                  <select
                    value={draft.entityId}
                    onChange={(e) => setDraft({ ...draft, entityId: e.target.value })}
                  >
                    <option value="">—</option>
                    {manifest.entities.map((e) => (
                      <option key={e.id} value={e.id}>{e.id}</option>
                    ))}
                  </select>
                </label>
              )}

              {/* ACTIONS_FOR_KIND.entity's "Edit fields" card only stages add-field as its
                  default action; this toggle is the only remaining way to reach
                  remove-field/rename-field/change-type now that the old flat op-kind
                  dropdown (their other entry point) is gone. */}
              {['add-field', 'remove-field', 'rename-field', 'change-type'].includes(draft.kind) && (
                <fieldset>
                  <legend>Field operation</legend>
                  <div className="field-op-toggle">
                    {['add-field', 'remove-field', 'rename-field', 'change-type'].map((op) => (
                      <label key={op} className="sandbox-checkbox">
                        <input
                          type="radio"
                          name="field-op"
                          value={op}
                          checked={draft.kind === op}
                          onChange={(e) => setDraft({ ...draft, kind: e.target.value as OpKind })}
                        />
                        {op === 'add-field' && 'Add'}
                        {op === 'remove-field' && 'Remove'}
                        {op === 'rename-field' && 'Rename'}
                        {op === 'change-type' && 'Change type'}
                      </label>
                    ))}
                  </div>
                </fieldset>
              )}

              {draft.kind === 'add-field' && (
                <>
                  <label>
                    Field name
                    <input
                      type="text"
                      value={draft.fieldName}
                      onChange={(e) => setDraft({ ...draft, fieldName: e.target.value })}
                    />
                  </label>
                  <label>
                    Type
                    <input
                      type="text"
                      value={draft.addType}
                      onChange={(e) => setDraft({ ...draft, addType: e.target.value })}
                    />
                  </label>
                  <label className="sandbox-checkbox">
                    <input
                      type="checkbox"
                      checked={draft.optional}
                      onChange={(e) => setDraft({ ...draft, optional: e.target.checked })}
                    />
                    Optional
                  </label>
                </>
              )}

              {draft.kind === 'remove-field' && (
                <label>
                  Field
                  <FieldSelect
                    manifest={manifest}
                    entityId={draft.entityId}
                    value={draft.fieldName}
                    onChange={(v) => setDraft({ ...draft, fieldName: v })}
                  />
                </label>
              )}

              {draft.kind === 'rename-field' && (
                <>
                  <label>
                    From field
                    <FieldSelect
                      manifest={manifest}
                      entityId={draft.entityId}
                      value={draft.from}
                      onChange={(v) => setDraft({ ...draft, from: v })}
                    />
                  </label>
                  <label>
                    To
                    <input
                      type="text"
                      value={draft.to}
                      onChange={(e) => setDraft({ ...draft, to: e.target.value })}
                    />
                  </label>
                </>
              )}

              {draft.kind === 'change-type' && (
                <>
                  <label>
                    Field
                    <FieldSelect
                      manifest={manifest}
                      entityId={draft.entityId}
                      value={draft.fieldName}
                      onChange={(v) => setDraft({ ...draft, fieldName: v })}
                    />
                  </label>
                  <label>
                    New type
                    <input
                      type="text"
                      value={draft.newType}
                      onChange={(e) => setDraft({ ...draft, newType: e.target.value })}
                    />
                  </label>
                </>
              )}

              {draft.kind === 'create-entity' && (
                <>
                  <label>
                    New entity id
                    <input
                      type="text"
                      value={draft.newId}
                      onChange={(e) => setDraft({ ...draft, newId: e.target.value })}
                    />
                  </label>
                  <label>
                    Entity name
                    <input
                      type="text"
                      value={draft.newName}
                      onChange={(e) => setDraft({ ...draft, newName: e.target.value })}
                    />
                  </label>
                  <label>
                    Domain
                    <select
                      value={draft.newDomain}
                      onChange={(e) => setDraft({ ...draft, newDomain: e.target.value })}
                    >
                      <option value="">—</option>
                      {manifest.domains.map((d) => (
                        <option key={d.id} value={d.id}>{d.id}</option>
                      ))}
                    </select>
                  </label>
                  <label>
                    Description
                    <input
                      type="text"
                      value={draft.newDescription}
                      onChange={(e) => setDraft({ ...draft, newDescription: e.target.value })}
                    />
                  </label>
                </>
              )}

              {draft.kind === 'create-domain' && (
                <>
                  <label>
                    New domain id
                    <input
                      type="text"
                      value={draft.newId}
                      onChange={(e) => setDraft({ ...draft, newId: e.target.value })}
                    />
                  </label>
                  <label>
                    Domain name
                    <input
                      type="text"
                      value={draft.newName}
                      onChange={(e) => setDraft({ ...draft, newName: e.target.value })}
                    />
                  </label>
                  <label>
                    Color
                    <input
                      type="text"
                      value={draft.newColor}
                      onChange={(e) => setDraft({ ...draft, newColor: e.target.value })}
                    />
                  </label>
                  <label>
                    Description
                    <input
                      type="text"
                      value={draft.newDescription}
                      onChange={(e) => setDraft({ ...draft, newDescription: e.target.value })}
                    />
                  </label>
                </>
              )}

              {draft.kind === 'create-reference' && (
                <>
                  <label>
                    From entity
                    <select
                      value={draft.newFromEntity}
                      onChange={(e) => setDraft({ ...draft, newFromEntity: e.target.value })}
                    >
                      <option value="">—</option>
                      {manifest.entities.map((e) => (
                        <option key={e.id} value={e.id}>{e.id}</option>
                      ))}
                    </select>
                  </label>
                  <label>
                    To entity
                    <select
                      value={draft.newToEntity}
                      onChange={(e) => setDraft({ ...draft, newToEntity: e.target.value })}
                    >
                      <option value="">—</option>
                      {manifest.entities.map((e) => (
                        <option key={e.id} value={e.id}>{e.id}</option>
                      ))}
                    </select>
                  </label>
                  <label>
                    From field
                    <input
                      type="text"
                      value={draft.newFromField}
                      onChange={(e) => setDraft({ ...draft, newFromField: e.target.value })}
                    />
                  </label>
                  <label>
                    Reference kind
                    <select
                      value={draft.newReferenceKind}
                      onChange={(e) => setDraft({ ...draft, newReferenceKind: e.target.value })}
                    >
                      <option value="">—</option>
                      {REFERENCE_KINDS.map((k) => (
                        <option key={k} value={k}>{k}</option>
                      ))}
                    </select>
                  </label>
                </>
              )}

              {draft.kind === 'create-surface' && (
                <>
                  <label>
                    New surface id
                    <input
                      type="text"
                      value={draft.newId}
                      onChange={(e) => setDraft({ ...draft, newId: e.target.value })}
                    />
                  </label>
                  <label>
                    Surface type
                    <select
                      value={draft.newSurfaceType}
                      onChange={(e) => setDraft({ ...draft, newSurfaceType: e.target.value })}
                    >
                      <option value="">—</option>
                      {SURFACE_TYPES.map((t) => (
                        <option key={t} value={t}>{t}</option>
                      ))}
                    </select>
                  </label>
                  <label>
                    Domain
                    <select
                      value={draft.newDomain}
                      onChange={(e) => setDraft({ ...draft, newDomain: e.target.value })}
                    >
                      <option value="">—</option>
                      {manifest.domains.map((d) => (
                        <option key={d.id} value={d.id}>{d.id}</option>
                      ))}
                    </select>
                  </label>
                  <label>
                    Hub
                    <input
                      type="text"
                      value={draft.newHub}
                      onChange={(e) => setDraft({ ...draft, newHub: e.target.value })}
                    />
                  </label>
                </>
              )}

              {draft.kind === 'create-cloud-function' && (
                <>
                  <label>
                    Function name
                    <input
                      type="text"
                      value={draft.newId}
                      onChange={(e) => setDraft({ ...draft, newId: e.target.value })}
                    />
                  </label>
                  <label>
                    Function kind
                    <select
                      value={draft.newFunctionKind}
                      onChange={(e) => setDraft({ ...draft, newFunctionKind: e.target.value })}
                    >
                      <option value="">—</option>
                      {CLOUD_FUNCTION_KINDS.map((k) => (
                        <option key={k} value={k}>{k}</option>
                      ))}
                    </select>
                  </label>
                  <label>
                    Domain
                    <select
                      value={draft.newDomain}
                      onChange={(e) => setDraft({ ...draft, newDomain: e.target.value })}
                    >
                      <option value="">—</option>
                      {manifest.domains.map((d) => (
                        <option key={d.id} value={d.id}>{d.id}</option>
                      ))}
                    </select>
                  </label>
                </>
              )}

              {draft.kind === 'create-event' && (
                <>
                  <label>
                    Event type
                    <input
                      type="text"
                      value={draft.newId}
                      onChange={(e) => setDraft({ ...draft, newId: e.target.value })}
                    />
                  </label>
                  <label>
                    Category
                    <input
                      type="text"
                      value={draft.newCategory}
                      onChange={(e) => setDraft({ ...draft, newCategory: e.target.value })}
                    />
                  </label>
                  <label>
                    Safety tier
                    <select
                      value={draft.newSafetyTier}
                      onChange={(e) => setDraft({ ...draft, newSafetyTier: e.target.value })}
                    >
                      <option value="">—</option>
                      {EVENT_SAFETY_TIERS.map((t) => (
                        <option key={t} value={t}>{t}</option>
                      ))}
                    </select>
                  </label>
                </>
              )}

              {draft.kind === 'create-path' && (
                <>
                  <label>
                    New path
                    <input
                      type="text"
                      value={draft.newPath}
                      onChange={(e) => setDraft({ ...draft, newPath: e.target.value })}
                    />
                  </label>
                  <label>
                    Retention
                    <input
                      type="text"
                      value={draft.newRetention}
                      onChange={(e) => setDraft({ ...draft, newRetention: e.target.value })}
                    />
                  </label>
                </>
              )}

              {draft.kind === 'create-tag' && (
                <>
                  <label>
                    New tag
                    <input
                      type="text"
                      value={draft.newTag}
                      onChange={(e) => setDraft({ ...draft, newTag: e.target.value })}
                    />
                  </label>
                  <label>
                    Description
                    <input
                      type="text"
                      value={draft.newDescription}
                      onChange={(e) => setDraft({ ...draft, newDescription: e.target.value })}
                    />
                  </label>
                </>
              )}

              {draft.kind === 'create-state-machine' && (
                <>
                  <label>
                    New state machine id
                    <input
                      type="text"
                      value={draft.newId}
                      onChange={(e) => setDraft({ ...draft, newId: e.target.value })}
                    />
                  </label>
                  <label>
                    State machine name
                    <input
                      type="text"
                      value={draft.newName}
                      onChange={(e) => setDraft({ ...draft, newName: e.target.value })}
                    />
                  </label>
                  <label>
                    Domain
                    <select
                      value={draft.newDomain}
                      onChange={(e) => setDraft({ ...draft, newDomain: e.target.value })}
                    >
                      <option value="">—</option>
                      {manifest.domains.map((d) => (
                        <option key={d.id} value={d.id}>{d.id}</option>
                      ))}
                    </select>
                  </label>
                </>
              )}

              {draft.kind === 'update-entity' && (
                <>
                  <label>
                    Entity id
                    <input type="text" value={draft.entityId} onChange={(e) => setDraft({ ...draft, entityId: e.target.value })} />
                  </label>
                  <label>
                    Name
                    <input type="text" value={draft.updateName} onChange={(e) => setDraft({ ...draft, updateName: e.target.value })} />
                  </label>
                  <label>
                    Domain
                    <select value={draft.updateDomain} onChange={(e) => setDraft({ ...draft, updateDomain: e.target.value })}>
                      <option value="">—</option>
                      {manifest.domains.map((d) => <option key={d.id} value={d.id}>{d.id}</option>)}
                    </select>
                  </label>
                  <label>
                    Description
                    <input type="text" value={draft.updateDescription} onChange={(e) => setDraft({ ...draft, updateDescription: e.target.value })} />
                  </label>
                </>
              )}

              {draft.kind === 'update-surface' && (
                <>
                  <label>
                    Surface id
                    <input type="text" value={draft.updateSelector} onChange={(e) => setDraft({ ...draft, updateSelector: e.target.value })} />
                  </label>
                  <label>
                    Surface type
                    <select value={draft.updateSurfaceType} onChange={(e) => setDraft({ ...draft, updateSurfaceType: e.target.value })}>
                      <option value="">—</option>
                      {SURFACE_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </label>
                  <label>
                    Domain
                    <select value={draft.updateDomain} onChange={(e) => setDraft({ ...draft, updateDomain: e.target.value })}>
                      <option value="">—</option>
                      {manifest.domains.map((d) => <option key={d.id} value={d.id}>{d.id}</option>)}
                    </select>
                  </label>
                  <label>
                    Hub
                    <input type="text" value={draft.updateHub} onChange={(e) => setDraft({ ...draft, updateHub: e.target.value })} />
                  </label>
                  <label>
                    Description
                    <input type="text" value={draft.updateDescription} onChange={(e) => setDraft({ ...draft, updateDescription: e.target.value })} />
                  </label>
                </>
              )}

              {draft.kind === 'update-cloud-function' && (
                <>
                  <label>
                    Function name
                    <input type="text" value={draft.updateSelector} onChange={(e) => setDraft({ ...draft, updateSelector: e.target.value })} />
                  </label>
                  <label>
                    Function kind
                    <select value={draft.updateFunctionKind} onChange={(e) => setDraft({ ...draft, updateFunctionKind: e.target.value })}>
                      <option value="">—</option>
                      {CLOUD_FUNCTION_KINDS.map((k) => <option key={k} value={k}>{k}</option>)}
                    </select>
                  </label>
                  <label>
                    Domain
                    <select value={draft.updateDomain} onChange={(e) => setDraft({ ...draft, updateDomain: e.target.value })}>
                      <option value="">—</option>
                      {manifest.domains.map((d) => <option key={d.id} value={d.id}>{d.id}</option>)}
                    </select>
                  </label>
                  <label>
                    Description
                    <input type="text" value={draft.updateDescription} onChange={(e) => setDraft({ ...draft, updateDescription: e.target.value })} />
                  </label>
                  <label>
                    Rationale
                    <input type="text" value={draft.updateRationale} onChange={(e) => setDraft({ ...draft, updateRationale: e.target.value })} />
                  </label>
                  <label>
                    Trigger condition
                    <input type="text" value={draft.updateTriggerCondition} onChange={(e) => setDraft({ ...draft, updateTriggerCondition: e.target.value })} />
                  </label>
                </>
              )}

              {draft.kind === 'update-event' && (
                <>
                  <label>
                    Event type
                    <input type="text" value={draft.updateSelector} onChange={(e) => setDraft({ ...draft, updateSelector: e.target.value })} />
                  </label>
                  <label>
                    Category
                    <input type="text" value={draft.updateCategory} onChange={(e) => setDraft({ ...draft, updateCategory: e.target.value })} />
                  </label>
                  <label>
                    Description
                    <input type="text" value={draft.updateDescription} onChange={(e) => setDraft({ ...draft, updateDescription: e.target.value })} />
                  </label>
                  <label>
                    Business trigger
                    <input type="text" value={draft.updateBusinessTrigger} onChange={(e) => setDraft({ ...draft, updateBusinessTrigger: e.target.value })} />
                  </label>
                  <label>
                    Retention (days)
                    <input type="number" value={draft.updateRetentionDays} onChange={(e) => setDraft({ ...draft, updateRetentionDays: e.target.value })} />
                  </label>
                </>
              )}

              {draft.kind === 'update-path' && (
                <>
                  <label>
                    Path
                    <input type="text" value={draft.updateSelector} onChange={(e) => setDraft({ ...draft, updateSelector: e.target.value })} />
                  </label>
                  <label>
                    Retention
                    <input type="text" value={draft.updateRetention} onChange={(e) => setDraft({ ...draft, updateRetention: e.target.value })} />
                  </label>
                  <label>
                    Security rule note
                    <input type="text" value={draft.updateSecurityRuleNote} onChange={(e) => setDraft({ ...draft, updateSecurityRuleNote: e.target.value })} />
                  </label>
                  <label>
                    Owner
                    <input type="text" value={draft.updateOwner} onChange={(e) => setDraft({ ...draft, updateOwner: e.target.value })} />
                  </label>
                  <label>
                    Quota notes
                    <input type="text" value={draft.updateQuotaNotes} onChange={(e) => setDraft({ ...draft, updateQuotaNotes: e.target.value })} />
                  </label>
                </>
              )}

              {(draft.kind === 'add-payload-field' || draft.kind === 'remove-payload-field' ||
                draft.kind === 'rename-payload-field' || draft.kind === 'change-payload-field-type') && (
                <>
                  <div className="current-fields-preview">
                    <div className="mock-legend">CURRENT PAYLOAD FIELDS</div>
                    {(draft.payloadFieldOwnerKind === 'surface'
                      ? surfaceById(manifest, draft.updateSelector)?.payloadFields
                      : eventTypeById(manifest, draft.updateSelector)?.payloadFields
                    )?.map((f) => (
                      <div key={f.name} className="field-row">
                        <span className="fname">{f.name}</span>
                        <span className="ftype">{f.type}</span>
                      </div>
                    )) ?? <p className="mock-hint">no payload fields yet</p>}
                  </div>
                  <label>
                    Action
                    <select
                      value={draft.kind}
                      onChange={(e) => setDraft({ ...draft, kind: e.target.value as OpKind })}
                    >
                      <option value="add-payload-field">Add field</option>
                      <option value="remove-payload-field">Remove field</option>
                      <option value="rename-payload-field">Rename field</option>
                      <option value="change-payload-field-type">Change field type</option>
                    </select>
                  </label>
                  {draft.kind === 'add-payload-field' && (
                    <>
                      <label>Field name<input type="text" value={draft.fieldName} onChange={(e) => setDraft({ ...draft, fieldName: e.target.value })} /></label>
                      <label>Field type<input type="text" value={draft.addType} onChange={(e) => setDraft({ ...draft, addType: e.target.value })} /></label>
                    </>
                  )}
                  {draft.kind === 'remove-payload-field' && (
                    <label>Field name<input type="text" value={draft.fieldName} onChange={(e) => setDraft({ ...draft, fieldName: e.target.value })} /></label>
                  )}
                  {draft.kind === 'rename-payload-field' && (
                    <>
                      <label>From<input type="text" value={draft.from} onChange={(e) => setDraft({ ...draft, from: e.target.value })} /></label>
                      <label>To<input type="text" value={draft.to} onChange={(e) => setDraft({ ...draft, to: e.target.value })} /></label>
                    </>
                  )}
                  {draft.kind === 'change-payload-field-type' && (
                    <>
                      <label>Field name<input type="text" value={draft.fieldName} onChange={(e) => setDraft({ ...draft, fieldName: e.target.value })} /></label>
                      <label>New type<input type="text" value={draft.newType} onChange={(e) => setDraft({ ...draft, newType: e.target.value })} /></label>
                    </>
                  )}
                </>
              )}

              {(draft.kind === 'add-function-entity-ref' || draft.kind === 'remove-function-entity-ref') && (
                <>
                  <div className="current-fields-preview">
                    <div className="mock-legend">CURRENT {draft.refArrayName.toUpperCase()}</div>
                    {(cloudFunctionById(manifest, draft.updateSelector)?.[draft.refArrayName || 'reads'] ?? []).map((id) => (
                      <div key={id} className="field-row"><span className="fname">{id}</span></div>
                    ))}
                  </div>
                  <label>
                    Action
                    <select value={draft.kind} onChange={(e) => setDraft({ ...draft, kind: e.target.value as OpKind })}>
                      <option value="add-function-entity-ref">Add entity</option>
                      <option value="remove-function-entity-ref">Remove entity</option>
                    </select>
                  </label>
                  <label>
                    Entity
                    <ObjectPicker
                      items={listPickable(manifest, 'entity')}
                      value={draft.refEntityId}
                      ariaLabel={`Entity to ${draft.kind === 'add-function-entity-ref' ? 'add to' : 'remove from'} ${draft.refArrayName}`}
                      onChange={(id) => setDraft({ ...draft, refEntityId: id })}
                    />
                  </label>
                </>
              )}

              {(draft.kind === 'add-event-carried-entity' || draft.kind === 'remove-event-carried-entity') && (
                <>
                  <div className="current-fields-preview">
                    <div className="mock-legend">CURRENT CARRIED ENTITIES</div>
                    {(eventTypeById(manifest, draft.updateSelector)?.carriedEntities ?? []).map((id) => (
                      <div key={id} className="field-row"><span className="fname">{id}</span></div>
                    ))}
                  </div>
                  <label>
                    Action
                    <select value={draft.kind} onChange={(e) => setDraft({ ...draft, kind: e.target.value as OpKind })}>
                      <option value="add-event-carried-entity">Add entity</option>
                      <option value="remove-event-carried-entity">Remove entity</option>
                    </select>
                  </label>
                  <label>
                    Entity
                    <ObjectPicker
                      items={listPickable(manifest, 'entity')}
                      value={draft.refEntityId}
                      ariaLabel="Entity to add or remove from carried entities"
                      onChange={(id) => setDraft({ ...draft, refEntityId: id })}
                    />
                  </label>
                </>
              )}

              <button
                className="btn btn-primary"
                onClick={addOp}
                disabled={!draftedIsValid}
              >
                <Icon name="add" size="sm" /> Add op
              </button>
            </fieldset>

            <div className="sandbox-op-list">
              <h4>Ops · {ops.length}</h4>
              {ops.length === 0 ? (
                <p className="sandbox-empty">No ops yet. Add one above.</p>
              ) : (
                <ol>
                  {ops.map((entry, i) => {
                    const effectiveState = getOpState(entry);
                    return (
                      <li key={`${i}-${summarizeOpText(entry.op)}`} className={`ai-op-row ai-op-row--${effectiveState}`}>
                        {(effectiveState === 'proposed' || effectiveState === 'accepted') && (
                          <span className="ai-star" title={effectiveState === 'proposed' ? 'AI-drafted suggestion' : undefined} style={{ color: effectiveState === 'accepted' ? 'var(--good)' : undefined }}>
                            <Icon
                              name={effectiveState === 'proposed' ? 'star' : 'check'}
                              size="sm"
                            />
                          </span>
                        )}
                        {effectiveState === 'readonly' && (
                          <span style={{ color: 'var(--text3)' }}>
                            <Icon name="check" size="sm" />
                          </span>
                        )}
                        <span className="mono">
                          <SummarizeOpRender op={entry.op} manifest={manifest} />
                        </span>
                        {effectiveState === 'proposed' && entry.confidence !== undefined && (
                          <span className="pill" style={{ fontSize: '10px' }}>{Math.round(entry.confidence * 100)}% confidence</span>
                        )}
                        {effectiveState === 'proposed' && (
                          <>
                            <button
                              className="btn btn-ghost btn-sm"
                              aria-label={`Accept op ${i + 1}`}
                              onClick={() => {
                                const updated = [...ops];
                                updated[i] = { ...entry, state: 'accepted' };
                                setOps(updated);
                              }}
                            >
                              Accept
                            </button>
                            <button
                              className="btn btn-ghost btn-sm"
                              aria-label={`Reject op ${i + 1}`}
                              onClick={() => removeOp(i)}
                            >
                              Reject
                            </button>
                          </>
                        )}
                        {effectiveState === 'accepted' && (
                          <button
                            className="btn btn-ghost btn-sm"
                            aria-label={`Remove op ${i + 1}`}
                            onClick={() => removeOp(i)}
                          >
                            Remove
                          </button>
                        )}
                      </li>
                    );
                  })}
                </ol>
              )}
            </div>
          </section>

          <section className="sandbox-diff" aria-label="Diff">
            <header className="sandbox-diff-head">
              <h3>Live diff</h3>
              <button
                type="button"
                className={`btn btn-ghost${rawMode ? ' is-active' : ''}`}
                onClick={() => (rawMode ? setRawMode(false) : openRaw())}
                aria-pressed={rawMode}
              >
                <Icon name="data_object" size="sm" />
                {rawMode ? 'Form mode' : 'Raw JSON'}
              </button>
            </header>

            {rawMode && (
              <div className="sandbox-raw" aria-label="Raw JSON editor">
                <textarea
                  className="sandbox-raw-input mono"
                  value={rawText}
                  onChange={(e) => setRawText(e.target.value)}
                  spellCheck={false}
                  rows={12}
                  aria-label="Raw ops JSON"
                />
                {rawError && <ErrorState variant="inline" message={rawError} />}
                <div className="sandbox-raw-actions">
                  <button type="button" className="btn btn-primary" onClick={applyRaw}>
                    Apply JSON
                  </button>
                  <button type="button" className="btn btn-ghost" onClick={() => setRawMode(false)}>
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {!rawMode && overlay.brokenRefs.length > 0 && (
              <div className="sandbox-overlay-warn" role="alert">
                <Icon name="warning" size="sm" />
                <div>
                  <strong>
                    {overlay.brokenRefs.length}{' '}
                    {overlay.brokenRefs.length === 1 ? 'reference breaks' : 'references break'}
                  </strong>
                  <ul className="sandbox-broken-list">
                    {overlay.brokenRefs.slice(0, 5).map((b, i) => (
                      <li key={i} className="mono">
                        {b.from}.{b.field} → {b.to} <em>· {b.reason}</em>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}

            {!rawMode && touchedEntities.length > 0 && (
              <div className="sandbox-overlay" aria-label="Constellation overlay">
                <span className="sandbox-overlay-label">
                  CONSTELLATION OVERLAY · {touchedEntities.length} affected
                </span>
                <div className="sandbox-overlay-legend">
                  <span><span className="sandbox-overlay-dot dot-add" /> added/affected</span>
                  <span><span className="sandbox-overlay-dot dot-rename" /> renamed</span>
                  <span><span className="sandbox-overlay-dot dot-break" /> broken edge</span>
                </div>
                <OverlaySvg
                  manifest={manifest}
                  touched={touchedEntities}
                  renamed={overlay.renamedFields.map((r) => r.entityId)}
                  broken={overlay.brokenRefs.map((b) => `${b.from}|${b.to}`)}
                />
              </div>
            )}

            {!rawMode &&
            touchedEntities.length === 0 &&
            touchedDomains.length === 0 &&
            createdReferences.length === 0 &&
            createdSurfaceIds.length === 0 &&
            createdFunctionNames.length === 0 &&
            createdEventTypes.length === 0 &&
            createdPaths.length === 0 &&
            createdTags.length === 0 &&
            createdMachineIds.length === 0 ? (
              <p className="sandbox-empty">Add an op to see the schema delta.</p>
            ) : (
              <>
                {!rawMode &&
                  touchedDomains.map((domainId) => {
                    const domain = domainById(afterManifest, domainId);
                    if (!domain) return null;
                    return (
                      <article key={domainId} className="sandbox-diff-entity">
                        <header>
                          <span className="mono">{domain.id}</span>
                          <span className="ref-kind">new domain</span>
                        </header>
                        <p className="sandbox-empty">{domain.description}</p>
                      </article>
                    );
                  })}
                {!rawMode &&
                  (() => {
                    const seen = new Set<string>();
                    return createdReferences
                      .filter((r) => {
                        const key = `${r.from}|${r.to}|${r.fromField}`;
                        if (seen.has(key)) return false;
                        seen.add(key);
                        return true;
                      })
                      .map((r) => (
                        <CreationCard
                          key={`${r.from}|${r.to}|${r.fromField}`}
                          id={`${r.from}.${r.fromField} → ${r.to}`}
                          label="new reference"
                          description={`${r.referenceKind} reference`}
                        />
                      ));
                  })()}
                {!rawMode &&
                  createdSurfaceIds.map((id) => {
                    const surface = afterManifest.surfaces.find((s) => s.id === id);
                    if (!surface) return null;
                    return (
                      <CreationCard key={id} id={surface.id} label="new surface" description={`${surface.type} · ${surface.domain}`} />
                    );
                  })}
                {!rawMode &&
                  createdFunctionNames.map((name) => {
                    const fn = afterManifest.cloudFunctions.find((f) => f.name === name);
                    if (!fn) return null;
                    return <CreationCard key={name} id={fn.name} label="new function" description={`${fn.kind} · ${fn.domain}`} />;
                  })}
                {!rawMode &&
                  createdEventTypes.map((type) => {
                    const ev = afterManifest.events.find((e) => e.type === type);
                    if (!ev) return null;
                    return <CreationCard key={type} id={ev.type} label="new event" description={ev.category} />;
                  })}
                {!rawMode &&
                  createdPaths.map((path) => {
                    const p = afterManifest.paths.find((x) => x.path === path);
                    if (!p) return null;
                    return <CreationCard key={path} id={p.path} label="new path" description={`${p.entityId} · ${p.retention}`} />;
                  })}
                {!rawMode &&
                  createdTags.map((tag) => {
                    const t = afterManifest.tags.find((x) => x.tag === tag);
                    if (!t) return null;
                    return <CreationCard key={tag} id={t.tag} label="new tag" description={t.description} />;
                  })}
                {!rawMode &&
                  createdMachineIds.map((id) => {
                    const m = afterManifest.stateMachines.find((x) => x.id === id);
                    if (!m) return null;
                    return <CreationCard key={id} id={m.id} label="new state machine" description={m.name} />;
                  })}
                {!rawMode &&
                  touchedEntities.map((entityId) => {
                    const before = entityById(manifest, entityId);
                    const after = entityById(afterManifest, entityId);
                    if (!after) return null;
                    if (!before) {
                      return (
                        <article key={entityId} className="sandbox-diff-entity">
                          <header>
                            <span className="mono">
                              <EntityChip entityId={entityId} manifest={manifest} size="sm" />
                            </span>
                            <span className="ref-kind">new entity</span>
                          </header>
                          <p className="sandbox-empty">{after.description}</p>
                        </article>
                      );
                    }
                    const diff = diffEntity(before, after);
                    return (
                      <article key={entityId} className="sandbox-diff-entity">
                        <header>
                          <button
                            className="sandbox-diff-entity-link"
                            onClick={() => onNavigateEntity(entityId)}
                          >
                            {entityId}
                          </button>
                        </header>
                        {diff.added.map((f) => (
                          <FieldRow key={`a-${f.name}`} field={f} tone="added" />
                        ))}
                        {diff.removed.map((f) => (
                          <FieldRow key={`r-${f.name}`} field={f} tone="removed" />
                        ))}
                        {diff.changed.map((c) => (
                          <div key={`c-${c.name}`} className="sandbox-diff-row tone-changed">
                            <span className="mono">{c.name}</span>
                            <span className="mono before">{c.beforeType}</span>
                            <span className="arrow">→</span>
                            <span className="mono after">{c.afterType}</span>
                          </div>
                        ))}
                        {(diff.added.length + diff.removed.length + diff.changed.length === 0) && (
                          <p className="sandbox-empty">No net change after dedup.</p>
                        )}
                      </article>
                    );
                  })}
              </>
            )}
          </section>
        </>
      ) : (
        <section className="sandbox-empty-pane" aria-label="No sandbox selected">
          <p>Pick a sandbox on the left, or create a new one.</p>
        </section>
      )}
    </div>
  );
}

function FieldSelect({
  manifest,
  entityId,
  value,
  onChange,
}: {
  manifest: AtlasManifest;
  entityId: string;
  value: string;
  onChange: (v: string) => void;
}): React.JSX.Element {
  const entity = entityById(manifest, entityId);
  const fields = entity?.fields ?? [];
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)}>
      <option value="">—</option>
      {fields.map((f) => (
        <option key={f.name} value={f.name}>{f.name}</option>
      ))}
    </select>
  );
}

function FieldRow({ field, tone }: { field: Field; tone: 'added' | 'removed' }): React.JSX.Element {
  return (
    <div className={`sandbox-diff-row tone-${tone}`}>
      <span className="sandbox-diff-marker">{tone === 'added' ? '+' : '−'}</span>
      <span className="mono">{field.name}</span>
      <span className="mono type">{field.type}</span>
    </div>
  );
}

/** One card in the diff pane for any newly-created (never before-existing)
 *  record — reference, surface, cloud function, event, path, tag, or state
 *  machine. Entities and domains render their own inline cards (they can
 *  also participate in field-level ops), but these 7 kinds are pure
 *  creations, so one shared shape covers all of them. */
function CreationCard({ id, label, description }: { id: string; label: string; description: string }): React.JSX.Element {
  return (
    <article className="sandbox-diff-entity">
      <header>
        <span className="mono">{id}</span>
        <span className="ref-kind">{label}</span>
      </header>
      <p className="sandbox-empty">{description}</p>
    </article>
  );
}

/**
 * Constellation overlay (PR Q4 · §Q4.3). Lightweight SVG: shows the
 * touched entities + their 1-hop neighbours, colour-coded by overlay
 * state (added/affected, renamed, broken edge). Not a full Sigma view
 * — that's deliberately out of scope to keep the sandbox chunk light.
 */
function OverlaySvg({
  manifest,
  touched,
  renamed,
  broken,
}: {
  manifest: AtlasManifest;
  touched: string[];
  renamed: string[];
  broken: string[];
}): React.JSX.Element {
  const touchedSet = new Set(touched);
  const renamedSet = new Set(renamed);
  const brokenSet = new Set(broken);

  // Collect 1-hop neighbours of touched entities.
  const neighbours = new Set<string>();
  for (const ref of manifest.references) {
    if (touchedSet.has(ref.from)) neighbours.add(ref.to);
    if (touchedSet.has(ref.to)) neighbours.add(ref.from);
  }
  for (const t of touchedSet) neighbours.delete(t);

  const nodes = [...touchedSet, ...neighbours];
  if (nodes.length === 0) return <></>;

  // Place nodes in a simple circular layout.
  const cx = 200;
  const cy = 120;
  const radius = nodes.length > 1 ? 90 : 0;
  const positions = new Map<string, { x: number; y: number }>();
  nodes.forEach((id, i) => {
    const angle = (2 * Math.PI * i) / nodes.length - Math.PI / 2;
    positions.set(id, {
      x: cx + radius * Math.cos(angle),
      y: cy + radius * Math.sin(angle),
    });
  });

  // Edges among visible nodes.
  const edges = manifest.references.filter(
    (r) => positions.has(r.from) && positions.has(r.to),
  );

  const colorFor = (id: string): string => {
    if (brokenSet.has(id)) return 'var(--danger)';
    if (renamedSet.has(id)) return 'var(--warn)';
    if (touchedSet.has(id)) return 'var(--good)';
    return 'var(--text3)';
  };

  return (
    <svg viewBox="0 0 400 240" className="sandbox-overlay-svg" aria-label="Sandbox overlay">
      <defs>
        <marker id="sandbox-arrow" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="6" markerHeight="6" orient="auto">
          <path d="M 0 0 L 10 5 L 0 10 z" fill="var(--text4)" />
        </marker>
      </defs>
      {edges.map((e, i) => {
        const a = positions.get(e.from)!;
        const b = positions.get(e.to)!;
        const isBroken = brokenSet.has(`${e.from}|${e.to}`);
        return (
          <line
            key={i}
            x1={a.x}
            y1={a.y}
            x2={b.x}
            y2={b.y}
            stroke={isBroken ? 'var(--danger)' : 'var(--text4)'}
            strokeWidth={isBroken ? 2 : 1}
            strokeDasharray={isBroken ? '4 4' : undefined}
            markerEnd="url(#sandbox-arrow)"
            opacity={isBroken ? 0.8 : 0.35}
          />
        );
      })}
      {nodes.map((id) => {
        const p = positions.get(id)!;
        const isTouched = touchedSet.has(id);
        return (
          <g key={id} transform={`translate(${p.x}, ${p.y})`}>
            <circle
              r={isTouched ? 16 : 10}
              fill={isTouched ? colorFor(id) : 'transparent'}
              fillOpacity={isTouched ? 0.18 : 0}
              stroke={colorFor(id)}
              strokeWidth={isTouched ? 2 : 1.5}
            />
            <text
              y={isTouched ? 4 : 3}
              textAnchor="middle"
              fontFamily="var(--ff-mono)"
              fontSize={isTouched ? 11 : 9}
              fill={isTouched ? 'var(--text)' : 'var(--text3)'}
            >
              {id.length > 14 ? id.slice(0, 13) + '…' : id}
            </text>
          </g>
        );
      })}
    </svg>
  );
}
